import { AssessmentTab } from "../types";
import { CheckboxHierarchyState } from "../hooks/useCheckboxHierarchy";
import { buildLayerPath, buildSectionPath } from "../useSiteReportConfiguration/pathUtils";
import { getTabLayerPaths, getGroupLayerPaths, getGroupSectionPaths, collectLayerPaths } from "../utils";

export interface ToggleParams {
  tabKey: string;
  layerId?: string;
  groupId?: string;
}

/**
 * Interface defining the contract for toggle strategies used in the Site Report.
 *
 * Simplified interface that focuses only on toggle behavior. Layer collection
 * and path building are now handled by dedicated utility functions.
 */
export interface ToggleStrategy {
  toggle(
    params: ToggleParams,
    assessmentTabs: AssessmentTab[],
    checkboxState: CheckboxHierarchyState,
    allLayerIds?: string[]
  ): void;

  buildPaths(assessmentTabs: AssessmentTab[]): string[];
}

/**
 * Layer-based toggle strategy that manages checkbox states hierarchically.
 *
 * This strategy is used when each layer should be toggled independently while maintaining
 * parent-child relationships between tabs, groups, and layers. It ensures that parent
 * checkboxes (tabs/groups) reflect the correct state based on their children.
 *
 * Use cases:
 * - When you need granular control over individual layers
 * - When parent states should automatically update based on child selections
 * - For simple checkbox hierarchies without sections
 */
export class LayerBasedToggleStrategy implements ToggleStrategy {
  toggle(
    params: ToggleParams,
    assessmentTabs: AssessmentTab[],
    checkboxState: CheckboxHierarchyState,
    allLayerIds: string[]
  ): void {
    const { tabKey, layerId, groupId } = params;

    if (layerId) {
      const path = buildLayerPath(tabKey, layerId);
      checkboxState.toggleCheckbox(path);
      checkboxState.updateParentState(path, allLayerIds);
    } else if (groupId) {
      const groupPaths = getGroupLayerPaths(assessmentTabs, tabKey, groupId);
      const allChecked = groupPaths.every((path) => checkboxState.getCheckboxState(path));

      groupPaths.forEach((path) => {
        checkboxState.setCheckboxState(path, !allChecked);
      });

      groupPaths.forEach((path) => {
        checkboxState.updateParentState(path, allLayerIds);
      });
    } else {
      const tabPaths = getTabLayerPaths(assessmentTabs, tabKey);
      const allChecked = tabPaths.every((path) => checkboxState.getCheckboxState(path));

      tabPaths.forEach((path) => {
        checkboxState.setCheckboxState(path, !allChecked);
      });

      tabPaths.forEach((path) => {
        checkboxState.updateParentState(path, allLayerIds);
      });
    }
  }

  buildPaths(assessmentTabs: AssessmentTab[]): string[] {
    return collectLayerPaths(assessmentTabs, (layerId, tab) => buildLayerPath(tab.key, layerId));
  }
}

/**
 * Section-based toggle strategy that supports an additional layer of hierarchy through sections.
 *
 * This strategy is used when groups can contain sections, adding an extra level between
 * groups and layers. It handles both section-based and direct layer-based structures,
 * making it more flexible than LayerBasedToggleStrategy.
 *
 * Use cases:
 * - When you need to organize layers into sections within groups
 * - When some groups have sections while others have direct layer children
 * - For complex UI structures where bulk operations on sections are needed
 * - When a single section selection should activate all associated layers
 *
 * Key differences from LayerBasedToggleStrategy:
 * - Supports section-level checkboxes that control multiple layers at once
 * - Uses bulk state updates for better performance with many checkboxes
 * - Handles mixed structures (some groups with sections, some without)
 */
export class SectionBasedToggleStrategy implements ToggleStrategy {
  toggle(params: ToggleParams, assessmentTabs: AssessmentTab[], checkboxState: CheckboxHierarchyState): void {
    const { tabKey, layerId, groupId } = params;
    const updates: Record<string, boolean> = {};

    if (layerId) {
      const path = buildLayerPath(tabKey, layerId);
      const currentState = checkboxState.getCheckboxState(path);
      updates[path] = !currentState;
    } else if (groupId) {
      const sectionPaths = getGroupSectionPaths(assessmentTabs, tabKey, groupId);
      const layerPaths = getGroupLayerPaths(assessmentTabs, tabKey, groupId);

      // Use section paths if available, otherwise use layer paths
      const groupPaths = sectionPaths.length > 0 ? sectionPaths : layerPaths;
      const allChecked = groupPaths.every((path) => checkboxState.getCheckboxState(path));

      groupPaths.forEach((path) => {
        updates[path] = !allChecked;
      });
    } else {
      // For tab-level toggling, collect all paths (sections and layers)
      const tabPaths: string[] = [];

      // Add section paths for groups that have them, otherwise add layer paths
      assessmentTabs
        .find((t) => t.key === tabKey)
        ?.groups.forEach((group) => {
          const sectionPaths = getGroupSectionPaths(assessmentTabs, tabKey, group.id);
          if (sectionPaths.length > 0) {
            tabPaths.push(...sectionPaths);
          } else {
            const groupLayerPaths = getGroupLayerPaths(assessmentTabs, tabKey, group.id);
            tabPaths.push(...groupLayerPaths);
          }
        });

      const allChecked = tabPaths.every((path) => checkboxState.getCheckboxState(path));

      tabPaths.forEach((path) => {
        updates[path] = !allChecked;
      });
    }

    checkboxState.setBulkCheckboxState(updates);
  }

  buildPaths(assessmentTabs: AssessmentTab[]): string[] {
    const paths: string[] = [];

    assessmentTabs.forEach((tab) => {
      tab.groups.forEach((group) => {
        if (group.sectionIds && group.sectionIds.length > 0) {
          // Use section paths when sections exist
          group.sectionIds.forEach((sectionId) => {
            paths.push(buildSectionPath(tab.key, group.id, sectionId));
          });
        } else {
          // Fall back to layer paths when no sections
          group.layerIds.forEach((layerId) => {
            paths.push(buildLayerPath(tab.key, layerId));
          });
        }
      });
    });

    return paths;
  }
}
